import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { FollowUpAction } from '../../../types/followup';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { TextArea } = Input;

const FollowUpActionList: React.FC = () => {
  const [actionList, setActionList] = useState<FollowUpAction[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAction, setEditingAction] = useState<FollowUpAction | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadFUAction.create');
  const canUpdate = hasPermission('leadFUAction.update');
  const canDelete = hasPermission('leadFUAction.delete');

  const fetchActions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-follow-up-actions');
      if (response.data && response.data.status === 'success') {
        setActionList(response.data.data);
      } else {
        setActionList([]);
        message.error('Gagal memuat data action follow-up');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Gagal mengambil data action follow-up';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const showModal = (action: FollowUpAction | null = null) => {
    setEditingAction(action);
    if (action) {
      form.setFieldsValue({
        name: action.name,
        description: action.description,
      });
    } else {
      form.setFieldsValue({
        name: '',
        description: '',
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingAction(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name,
        description: values.description,
      };

      if (editingAction) {
        await api.put(`lead-follow-up-actions/${editingAction.id}`, payload);
        message.success('Action Follow Up berhasil diperbarui');
      } else {
        await api.post('lead-follow-up-actions', payload);
        message.success('Action Follow Up berhasil ditambahkan');
      }

      handleCancel();
      fetchActions(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: FollowUpAction) => {
    try {
      setLoading(true);
      const actionVerb = record.is_active ? 'deactivate' : 'reactivate';
      const endpoint = record.is_active
        ? `/lead-follow-up-actions/${record.id}`
        : `/lead-follow-up-actions/${record.id}/reactivate`;

        if (record.is_active) {
          await api.delete(endpoint);
        } else {
          await api.put(endpoint);
        }

        message.success(`Action Follow Up berhasil di-${actionVerb}`);
        fetchActions();
    } catch (error: any) {
      const actionVerb = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Gagal ${actionVerb} action Follow Up`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Action Follow Up',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: FollowUpAction) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Apakah Anda yakin ingin ${
                record.is_active ? 'deactivate' : 'reactivate'
              } action ini?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Follow Up Action Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Action Follow Up
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={actionList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingAction ? 'Edit Action Follow Up' : 'Tambah Action Follow Up Baru'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="followUpActionForm">
          <Form.Item
            name="name"
            label="Nama Action"
            rules={[{ required: true, message: 'Nama action tidak boleh kosong!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FollowUpActionList;
